import {
  WorksheetCostCategory,
  WorksheetCoverageUnit,
  WorksheetMaterialUnit,
  WorksheetDensityUnit,
  WorksheetHeaderUnit,
  WorksheetLabourUnit,
  WorksheetMaterialCalcMethod,
  WorksheetMaterialOutputUnit,
  WorksheetThicknessUnit,
} from "generated/graphql";

export const WORKSHEET_CATEGORY_ORDER: WorksheetCostCategory[] = [
  WorksheetCostCategory.LABOR,
  WorksheetCostCategory.MATERIAL,
  WorksheetCostCategory.TRUCKING,
  WorksheetCostCategory.EQUIPMENT,
  WorksheetCostCategory.OTHER,
];

export const WORKSHEET_CATEGORY_LABELS: Record<WorksheetCostCategory, string> = {
  [WorksheetCostCategory.LABOR]: "Labor",
  [WorksheetCostCategory.MATERIAL]: "Material",
  [WorksheetCostCategory.TRUCKING]: "Trucking",
  [WorksheetCostCategory.EQUIPMENT]: "Equipment",
  [WorksheetCostCategory.OTHER]: "Other",
};

export const HEADER_UNIT_OPTIONS = [
  { value: WorksheetHeaderUnit.EACH, label: "Each" },
  { value: WorksheetHeaderUnit.TON, label: "Ton" },
  { value: WorksheetHeaderUnit.GALLON, label: "Gallon" },
  { value: WorksheetHeaderUnit.BLOCK, label: "Block" },
  { value: WorksheetHeaderUnit.LINEAR_FOOT, label: "Lin Ft" },
  { value: WorksheetHeaderUnit.SQUARE_FOOT, label: "Sq Ft" },
  { value: WorksheetHeaderUnit.SQUARE_YARD, label: "Sq Yd" },
  { value: WorksheetHeaderUnit.PAIL, label: "Pail" },
];

export const LABOUR_UNIT_OPTIONS = [
  { value: WorksheetLabourUnit.HOURS, label: "Hours" },
  { value: WorksheetLabourUnit.DAYS, label: "Days" },
  { value: WorksheetLabourUnit.HALF_DAY, label: "Half Day" },
  { value: WorksheetLabourUnit.JOB, label: "Job" },
  { value: WorksheetLabourUnit.EACH, label: "Each" },
];

export { MATERIAL_UNIT_OPTIONS } from "constants/materialUnits";

export const COVERAGE_UNIT_OPTIONS = [
  { value: WorksheetCoverageUnit.SQUARE_FOOT, label: "Sq Ft" },
  { value: WorksheetCoverageUnit.SQUARE_YARD, label: "Sq Yd" },
  { value: WorksheetCoverageUnit.LINEAR_FOOT, label: "Lin Ft" },
];

export const THICKNESS_UNIT_OPTIONS = [
  { value: WorksheetThicknessUnit.INCH, label: "Inches" },
  { value: WorksheetThicknessUnit.FOOT, label: "Feet" },
];

export const OUTPUT_UNIT_OPTIONS = [
  { value: WorksheetMaterialOutputUnit.TON, label: "Ton" },
  { value: WorksheetMaterialOutputUnit.POUND, label: "Pound" },
];

export const CALC_METHOD_OPTIONS = [
  { value: WorksheetMaterialCalcMethod.MANUAL, label: "Manual" },
  { value: WorksheetMaterialCalcMethod.COVERAGE, label: "Coverage" },
  { value: WorksheetMaterialCalcMethod.DENSITY, label: "Weight / Density" },
  { value: WorksheetMaterialCalcMethod.THICKNESS_FACTOR, label: "Thickness factor" },
];

const ENUM_TO_RAW: Record<string, string> = {
  [WorksheetHeaderUnit.EACH]: "each",
  [WorksheetHeaderUnit.TON]: "ton",
  [WorksheetHeaderUnit.GALLON]: "gallon",
  [WorksheetHeaderUnit.BLOCK]: "block",
  [WorksheetHeaderUnit.LINEAR_FOOT]: "lin_ft",
  [WorksheetHeaderUnit.SQUARE_FOOT]: "sq_ft",
  [WorksheetHeaderUnit.SQUARE_YARD]: "sq_yd",
  [WorksheetHeaderUnit.PAIL]: "pail",
  [WorksheetLabourUnit.HOURS]: "hours",
  [WorksheetLabourUnit.DAYS]: "days",
  [WorksheetLabourUnit.HALF_DAY]: "half_day",
  [WorksheetLabourUnit.JOB]: "job",
  [WorksheetMaterialUnit.PALLET]: "pallet",
  [WorksheetMaterialUnit.TRUCK]: "truck",
  [WorksheetMaterialUnit.CUBIC_YARD]: "cubic_yd",
  [WorksheetMaterialUnit.POUND]: "pound",
  [WorksheetMaterialUnit.UNIT]: "unit",
  [WorksheetMaterialCalcMethod.MANUAL]: "manual",
  [WorksheetMaterialCalcMethod.COVERAGE]: "coverage",
  [WorksheetMaterialCalcMethod.DENSITY]: "density",
  [WorksheetMaterialCalcMethod.THICKNESS_FACTOR]: "thickness_factor",
  [WorksheetThicknessUnit.INCH]: "in",
  [WorksheetThicknessUnit.FOOT]: "ft",
  [WorksheetDensityUnit.LB_PER_CUFT]: "lb_per_cuft",
};

const RAW_TO_ENUM: Record<string, string> = Object.fromEntries(
  Object.entries(ENUM_TO_RAW).map(([enumName, raw]) => [raw, enumName])
);

export const enumToRaw = (value: string): string => ENUM_TO_RAW[value] ?? value.toLowerCase();

export const rawToEnum = (value?: string | null): string =>
  value ? RAW_TO_ENUM[value] ?? value.toUpperCase() : "";

export const unitLabel = (unit?: string | null): string => {
  if (!unit) {
    return "";
  }

  const enumValue = rawToEnum(unit);

  return HEADER_UNIT_OPTIONS.find((option) => option.value === enumValue)?.label ?? unit;
};
